import type { GeoJSONSource, Map as MapLibreMap } from 'maplibre-gl';
import type { Station } from '../model/types';
import { STATIONS_SOURCE, type StationState } from './stationsStyle';

/** Duration of a station fading in or out, ms. */
const FADE_MS = 300;

type ToData = (stations: readonly Station[]) => GeoJSON.FeatureCollection;

interface Fade {
  from: number;
  to: number;
  start: number;
}

const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

/**
 * Draws a changing set of stations, fading them in and out (through the `fade` feature state) instead
 * of letting them blink. Stations on their way out stay in the data until they have faded.
 */
export class StationFader {
  private readonly map: MapLibreMap;
  private toData: ToData = () => ({ type: 'FeatureCollection', features: [] });
  /** Wanted on the map now, in drawing order. */
  private targets: readonly Station[] = [];
  /** Drawn: the targets plus those fading out. */
  private drawn = new Map<string, Station>();
  private opacity = new Map<string, number>();
  private fades = new Map<string, Fade>();
  private frame = 0;

  constructor(map: MapLibreMap) {
    this.map = map;
  }

  /** Makes `stations` (in drawing order) the ones shown, drawn with `toData`; the rest fade out. */
  show(stations: readonly Station[], toData: ToData) {
    this.toData = toData;
    const now = performance.now();
    const wanted = new Set(stations.map((s) => s.id));
    for (const s of stations) {
      this.drawn.set(s.id, s);
      this.fadeTo(s.id, 1, now);
    }
    for (const id of this.drawn.keys()) if (!wanted.has(id)) this.fadeTo(id, 0, now);
    this.targets = stations;
    this.render();
    if (!this.frame) this.tick();
  }

  /** Removes everything at once, e.g. when the layer is hidden. */
  clear() {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.targets = [];
    this.drawn.clear();
    this.opacity.clear();
    this.fades.clear();
    this.map.removeFeatureState({ source: STATIONS_SOURCE });
    this.render();
  }

  dispose() {
    cancelAnimationFrame(this.frame);
  }

  private fadeTo(id: string, to: number, now: number) {
    const current = this.opacity.get(id) ?? 0;
    const heading = this.fades.get(id)?.to ?? current;
    if (heading === to) return;
    this.fades.set(id, { from: current, to, start: now });
  }

  private render() {
    const wanted = new Set(this.targets.map((s) => s.id));
    // Leaving stations go below the others.
    const leaving = [...this.drawn.values()].filter((s) => !wanted.has(s.id));
    this.map.getSource<GeoJSONSource>(STATIONS_SOURCE)?.setData(this.toData([...this.targets, ...leaving]));
  }

  private readonly tick = () => {
    this.frame = 0;
    const now = performance.now();
    const duration = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : FADE_MS;
    let removed = false;
    for (const [id, f] of this.fades) {
      const t = duration ? Math.min(1, (now - f.start) / duration) : 1;
      const value = f.from + (f.to - f.from) * easeInOut(t);
      this.opacity.set(id, value);
      this.map.setFeatureState({ source: STATIONS_SOURCE, id }, { fade: value } satisfies StationState);
      if (t < 1) continue;
      this.fades.delete(id);
      if (f.to === 0) {
        this.drawn.delete(id);
        this.opacity.delete(id);
        this.map.removeFeatureState({ source: STATIONS_SOURCE, id }, 'fade');
        removed = true;
      }
    }
    if (removed) this.render();
    if (this.fades.size) this.frame = requestAnimationFrame(this.tick);
  };
}
